import React, { useMemo } from "react";
import { Box, Typography, Divider, Button, Card, CardContent, Stack } from "@mui/material";
import DoneAllIcon from "@mui/icons-material/DoneAll";

import DateUtils from "../../../../utils/DateUtils";
import { resumenInventario, mapearLineasConDiferencia, describirResultado } from "../../helpers/transformers";
import { useStocks } from "../../hooks/useStocks";
import { conteosDeLinea, totalContado } from "../../helpers/conteos";
import EstadoInventarioBadge from "./EstadoInventarioBadge";
import DiferenciaBadge from "./DiferenciaBadge";
import ConteosPorNave from "./ConteosPorNave";
import StockAlmacenes from "./StockAlmacenes";
import UbicacionArticulo from "./UbicacionArticulo";

/**
 * Resultado de un inventario (cabecera, resumen y líneas con su diferencia),
 * con el mismo aspecto que DetalleTraspaso de traspasos
 */
const DetalleInventarioResultado = ({ inventario, onRevisar }) => {
    const resumen = useMemo(() => resumenInventario(inventario), [inventario]);
    const lineas = useMemo(() => mapearLineasConDiferencia(inventario.lineas), [inventario]);
    const { stocks, ubicaciones } = useStocks(inventario.lineas);

    return (
        <Box>
            <Box sx={{ p: 2, backgroundColor: "#f8f9fa", borderRadius: 2, border: "1px solid #e9ecef", mb: 3 }}>
                <Stack spacing={1.5}>
                    <Fila etiqueta="ID" valor={`#${inventario.id}`} />
                    <Fila etiqueta="Preparador" valor={inventario.preparadorNombre || "Sin asignar"} />
                    <Fila etiqueta="Creado" valor={DateUtils.formatearFecha(inventario.fechaCreacion)} />
                    {inventario.fechaLimite && (
                        <Fila etiqueta="Fecha límite" valor={DateUtils.formatearFecha(inventario.fechaLimite)} />
                    )}
                    {inventario.observaciones && <Fila etiqueta="Observaciones" valor={inventario.observaciones} />}
                    <Fila etiqueta="Estado" valor={<EstadoInventarioBadge estado={inventario.estado} />} />
                    <Fila etiqueta="Resultado" valor={describirResultado(resumen)} />
                </Stack>
            </Box>

            {onRevisar && (
                <Box sx={{ mb: 3 }}>
                    <Button
                        variant="contained"
                        startIcon={<DoneAllIcon />}
                        fullWidth
                        onClick={() => onRevisar(inventario)}
                        sx={{
                            textTransform: "none",
                            fontWeight: 600,
                            py: 1.25,
                            fontSize: 15,
                            backgroundColor: "#cc071e",
                            "&:hover": { backgroundColor: "#a30618" },
                        }}
                    >
                        Marcar como revisado
                    </Button>
                </Box>
            )}

            <Divider sx={{ mb: 3 }} />

            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#212529", mb: 2 }}>
                Líneas ({lineas.length})
            </Typography>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                {lineas.length === 0 ? (
                    <Typography variant="body2" sx={{ color: "#6c757d", textAlign: "center", py: 3 }}>
                        Sin líneas
                    </Typography>
                ) : (
                    lineas.map((linea) => (
                        <Card
                            key={linea.id}
                            variant="outlined"
                            sx={{ borderColor: "#e9ecef", "&:hover": { borderColor: "#dee2e6", boxShadow: "0 2px 4px rgba(0,0,0,0.05)" } }}
                        >
                            <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1, mb: 0.5 }}>
                                    <Typography sx={{ fontWeight: 600, color: "#212529", fontSize: 14 }}>
                                        {linea.referencia}
                                    </Typography>
                                    <DiferenciaBadge diferencia={linea.diferencia} contado={linea.contado} />
                                </Box>
                                <Typography variant="body2" sx={{ color: "#495057", fontSize: 13, mb: 0.5 }}>
                                    {linea.nombre || "Sin nombre"}
                                </Typography>
                                <Box sx={{ mb: 1 }}>
                                    <UbicacionArticulo ubicacion={ubicaciones[linea.referencia]} />
                                </Box>
                                <Box sx={{ mb: 1 }}>
                                    <ConteosPorNave conteos={conteosDeLinea(linea)} />
                                </Box>
                                {(conteosDeLinea(linea).length > 1 || linea.ean) && (
                                    <Typography variant="body2" sx={{ color: "#6c757d", fontSize: 12, mb: 1 }}>
                                        {[
                                            conteosDeLinea(linea).length > 1 && `Total contado: ${totalContado(conteosDeLinea(linea))}`,
                                            linea.ean && `EAN: ${linea.ean}`,
                                        ]
                                            .filter(Boolean)
                                            .join(" · ")}
                                    </Typography>
                                )}
                                <StockAlmacenes stockPorAlmacen={stocks[linea.referencia]} />
                            </CardContent>
                        </Card>
                    ))
                )}
            </Box>
        </Box>
    );
};

const Fila = ({ etiqueta, valor }) => (
    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2 }}>
        <Typography variant="body2" sx={{ fontSize: 13, fontWeight: 600, color: "#6c757d", minWidth: 110 }}>
            {etiqueta}:
        </Typography>
        {typeof valor === "string" || typeof valor === "number" || !valor ? (
            <Typography variant="body2" sx={{ textAlign: "right", fontWeight: 500, color: "#212529", fontSize: 14, flex: 1 }}>
                {valor || "-"}
            </Typography>
        ) : (
            valor
        )}
    </Box>
);

export default DetalleInventarioResultado;
